import { useEffect, useRef, useState, type ReactNode } from "react";
import { Stepper, type StepperStep } from "../components/Stepper";
import { Button } from "../components/Button";

// Row 22.6 — multi-step flow shell consuming Stepper (indicator-only, per its own docs: "does
// NOT render step content") + a step-content slot + Next/Back/Finish controls. Optional
// per-step validation (`validate`) can wrap `useZodForm`'s `form.trigger()` or any async check
// (e.g. a server round-trip slug-uniqueness call) — `useZodForm`'s zodResolver already supports
// async Zod `.refine()` at the resolver layer, so async validation works without extra plumbing
// here; a thrown validate is treated as a failed (blocking) validation, never propagated.
//
// This template OWNS focus-management + live-region announcements (pre-build critique, HIGH,
// fixed) — Stepper itself does neither. The effect below is keyed on `activeIndex`, which only
// changes AFTER `goNext` resolves a truthy `validate()` (or has none) — so a rejected transition
// never reaches it; focus-shift + announcement never fire for a blocked Next.
export interface WizardStep {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  content: ReactNode;
  /** Return false (or throw) to block advancing past this step. May be async. */
  validate?: () => boolean | Promise<boolean>;
}

export interface WizardTemplateProps {
  title: ReactNode;
  steps: WizardStep[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onFinish: () => void | Promise<void>;
  className?: string;
}

export function WizardTemplate({ title, steps, activeIndex, onActiveIndexChange, onFinish, className }: WizardTemplateProps) {
  const [validating, setValidating] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Value-comparison, not an invocation-count boolean (post-build review, MED, fixed): a
  // "have we mounted yet" boolean ref set inside the effect body breaks under React 18
  // StrictMode's dev-only double-invoke of mount effects (setup -> cleanup -> setup), which
  // would falsely treat the second synthetic invocation as a real transition. Comparing the
  // actual previous vs current activeIndex is invariant to how many times the effect itself
  // runs, since the prop value hasn't changed between the two synthetic invocations.
  const prevActiveIndexRef = useRef(activeIndex);

  const stepperSteps: StepperStep[] = steps.map((s) => ({ id: s.id, label: s.label, description: s.description }));
  const current = steps[activeIndex];
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === steps.length - 1;

  useEffect(() => {
    const prevActiveIndex = prevActiveIndexRef.current;
    prevActiveIndexRef.current = activeIndex;
    if (prevActiveIndex === activeIndex) return;
    headingRef.current?.focus();
    setAnnouncement(`Step ${activeIndex + 1} of ${steps.length}: ${typeof current?.label === "string" ? current.label : ""}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const goBack = () => {
    if (!isFirst && !validating) onActiveIndexChange(activeIndex - 1);
  };

  // `validating` now guards the ENTIRE async path, not just the validate() call (post-build
  // review, HIGH, fixed): a step with no `validate` fn previously left the Next/Finish button
  // fully enabled while `onFinish()` was pending, so a rapid double-click could fire `onFinish`
  // twice concurrently (duplicate submit). The button is now disabled for the full duration of
  // any Next/Finish transition, validated or not.
  const goNext = async () => {
    if (validating) return;
    setValidating(true);
    try {
      let ok = true;
      if (current?.validate) {
        try {
          ok = await current.validate();
        } catch {
          ok = false;
        }
      }
      if (!ok) return;
      if (isLast) {
        await onFinish();
      } else {
        onActiveIndexChange(activeIndex + 1);
      }
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className={className}>
      <h1 className="mb-4 text-xl font-semibold text-[var(--color-text-primary)]">{title}</h1>
      <Stepper steps={stepperSteps} activeIndex={activeIndex} />
      <div className="mt-6">
        <h2 ref={headingRef} tabIndex={-1} className="sr-only">
          {current?.label}
        </h2>
        {current?.content}
      </div>
      <p role="status" aria-live="polite" className="sr-only">
        {validating ? "Validating…" : announcement}
      </p>
      <div className="mt-6 flex items-center justify-between">
        <Button type="button" variant="secondary" onClick={goBack} disabled={isFirst || validating}>
          Back
        </Button>
        <Button type="button" onClick={goNext} loading={validating}>
          {isLast ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
}
